import { observable, computed } from "mobx";
import { DateTime } from "luxon";
import { localizationService } from "@state";
import { ISOToDisplayCode, getDisplayedAmount } from "@utils";
import { CashoutType } from "@gp/hub";
import { BetOffer } from "./BetOffer";
import { BetSlipStatus } from "@api-types/agency/bet-slip/BetSlipStatus";
import { ShopAddress } from "@api-types/bet-slip/ShopAddress";
import { BetSlipCombinations } from "@api-types/bet-slip/BetSlipCombinations";
import { BetSlipSecureWinSetting } from "@api-types/agency/autocashout";
import { BetSlipExtended } from "@api-types/agency/bet-slip";
import { BettingAccountType } from "@api-types/agency/bet-slip/BettingAccountType";
import { BetSlipType } from "@api-types/bet-slip/BetSlipType";
import { BetStatus } from "@api-types/bet-slip/BetStatus";
import { Player } from "@api-types/bet-slip/Player";
import {
	groupOffersByEvent,
	injectIsMultiToOffers,
} from "@utils/my-bets/helpers";
import { THEME_SETTINGS } from "@theme-settings";

export class Bet {
	betSlipCombinations: BetSlipCombinations[];
	betSlipNumber: string;
	betSlipStatus: BetSlipStatus;
	betSlipSubmitEnvironmentTypeAbrv: string;
	betSlipType: BetSlipType;
	betStatusId: string;
	bettingAccountType: BettingAccountType;
	bettingAccountTypeId: string;
	cashoutGain: number | null;
	cashoutTimeStamp?: DateTime | null;
	code: string;
	combinationBettingOffersBankCount: number;
	combinationBettingOffersCount: number;
	currency: string;
	dateCreated: string;
	dateUpdated: string;
	displayGain: string;
	displayGainBonus: string;
	displayGainTax: string;
	displayHandlingFee: string;
	displayMaxGain: string;
	displayPayment: string;
	displayPaymentPerCombination: string;
	displayPayout: string;
	eventCount: number;
	fiscalizationId: string;
	fiscalizationSentDate: DateTime | null;
	gain: number;
	gainBonus: number;
	gainBonusPercentage: number;
	gainTax: number;
	gainTaxPercentage: number;
	groupedOffersByEvent: BetOffer[][];
	handlingFee: number;
	handlingFeePercentage: number | null;
	hasBetBuilder: boolean;
	id: string;
	ipAddress: string;
	isBonus: boolean;
	isChangeAccepted?: boolean;
	isFreebet?: boolean;
	isLive: boolean;
	isSystem: boolean;
	isWege: boolean;
	isWalletManuallyDisabled: boolean | null;
	maxCoefficient: number;
	metadata?: any;
	minGain: number | null;
	payment: number;
	paymentBonus: number | null;
	paymentPerCombination: number;
	payout: number | null;
	payoutDate: DateTime | null;
	player?: Player;
	shopId: string;
	shopAddress: ShopAddress;
	slipStatusId: string;
	stake: number;
	stakePerCombination: number;
	stakeTaxPercentage: number | null;
	system: string;
	taxFreePayoutAmount: number | null;
	totalNumberOfCombinations: number;
	totalNumberOfOffersPerBetSlip: number;
	userId: string | null;
	liveBetSlipCountdown: number | null;

	constructor(bet: BetSlipExtended) {
		this.assignValues = this.assignValues.bind(this);
		this.assignValues(bet);

		this.isAutoCashoutSet =
			bet && bet.betSlipSecureWinSetting ? true : false;
	}

	@observable cType?: CashoutType;
	@observable isPaidOut: boolean;
	@observable maxGain: number;
	@observable displayedAmount: string | null;
	@observable calculatedCashoutAmount: number;
	@observable indicator: number;
	@observable isRemoved: boolean;
	@observable betStatus: BetStatus;
	@observable betSlipOffers: BetOffer[];
	@observable isCancellationAvailable: boolean;
	@observable isAutoCashoutSet: boolean;
	@observable betSlipSecureWinSetting?: BetSlipSecureWinSetting | null;

	//#region getters

	get formattedBetSystemString() {
		const interpolation = { escapeValue: false };
		if (this.system) {
			let systemString = `${this.system} / ${
				this.eventCount - this.combinationBettingOffersBankCount
			} = ${this.combinationBettingOffersCount}`;

			if (this.combinationBettingOffersBankCount > 0) {
				systemString = `B x ${this.combinationBettingOffersBankCount}, 
					${systemString}`;
			}

			return localizationService.t("COMMON.INFO_SYSTEM", {
				interpolation,
				0: `(${systemString})`,
			});
		}

		if (this.totalNumberOfCombinations > 1) {
			return localizationService.t("COMMON.INFO_COMBINATION_MULTI", {
				interpolation,
				0: this.totalNumberOfCombinations,
			});
		}

		return localizationService.t("COMMON.INFO_COMBINATION", {
			interpolation,
			0: this.totalNumberOfCombinations,
		});
	}

	get shouldShowBetSlipStatus() {
		return (
			this.betSlipStatus.abrv?.includes("cashout") ||
			this.betSlipStatus.abrv === "paid-out"
		);
	}

	@computed get isCashoutUnavailable() {
		return (
			this.cType !== CashoutType.JACKTIME ||
			this.displayedAmount == null ||
			Number(this.displayedAmount) < 1
		);
	}

	//#endregion getters

	//#region object assign

	assignValues(bet: BetSlipExtended) {
		this.isAutoCashoutSet =
			bet && bet.betSlipSecureWinSetting ? true : false;

		this.betSlipCombinations = bet.betSlipCombinations;

		this.betSlipOffers = bet.betSlipOffers.map(
			(offer) => new BetOffer(offer)
		);

		this.hasBetBuilder = this.betSlipOffers.some(
			(betOffer) => betOffer.multiOffers && betOffer.multiOffers.length
		);

		this.groupedOffersByEvent = groupOffersByEvent(this.betSlipOffers);
		injectIsMultiToOffers(this.groupedOffersByEvent);

		this.betSlipSecureWinSetting = bet.betSlipSecureWinSetting;
		this.betSlipStatus = bet.betSlipStatus;
		this.betSlipType = bet.betSlipType;
		this.betStatusId = bet.betStatusId;
		this.player = bet.player;

		this.cashoutTimeStamp = DateTime.fromISO(bet.cashoutTimeStamp || "");
		this.betSlipNumber = bet.betSlipNumber;
		this.betSlipSubmitEnvironmentTypeAbrv =
			bet.betSlipSubmitEnvironmentTypeAbrv;
		this.betStatus = bet.betStatus;
		this.bettingAccountType = bet.bettingAccountType;
		this.bettingAccountTypeId = bet.bettingAccountTypeId
			? bet.bettingAccountTypeId
			: "";
		this.calculatedCashoutAmount = bet.calculatedCashoutAmount
			? bet.calculatedCashoutAmount
			: 0;
		this.cashoutGain = bet.cashoutGain;
		this.code = bet.code;
		this.combinationBettingOffersBankCount =
			bet.combinationBettingOffersBankCount;
		this.combinationBettingOffersCount = bet.combinationBettingOffersCount;
		this.cType = bet.cType;
		this.eventCount = bet.eventCount;
		this.fiscalizationId = bet.fiscalizationId;
		this.fiscalizationSentDate = DateTime.fromISO(
			bet.fiscalizationSentDate || ""
		);
		this.gain = bet.gain;
		this.gainBonus = bet.gainBonus ? bet.gainBonus : 0;
		this.gainBonusPercentage = bet.gainBonusPercentage
			? bet.gainBonusPercentage
			: 0;
		this.gainTax = bet.gainTax ? bet.gainTax : 0;
		this.gainTaxPercentage = bet.gainTaxPercentage
			? bet.gainTaxPercentage
			: 0;
		this.handlingFee = bet.handlingFee ? bet.handlingFee : 0;
		this.handlingFeePercentage = bet.handlingFeePercentage;
		this.ipAddress = bet.ipAddress;
		this.isBonus = bet.isBonus;
		this.isCancellationAvailable = bet.isCancellationAvailable;
		this.isLive = bet.isLive;
		this.isFreebet = bet.isFreebet;
		this.isWege = bet.isWege;
		this.maxCoefficient = bet.maxCoefficient;
		this.maxGain = bet.maxGain;
		this.metadata = bet.metadata;
		this.minGain = bet.minGain;
		this.payment = bet.payment;
		this.paymentBonus = bet.paymentBonus;
		this.paymentPerCombination = bet.paymentPerCombination;
		this.payout = bet.payout;
		this.payoutDate = DateTime.fromISO(bet.payoutDate || "");
		this.slipStatusId = bet.slipStatusId;
		this.stake = bet.stake;
		this.stakePerCombination = bet.stakePerCombination;
		this.stakeTaxPercentage = bet.stakeTaxPercentage;
		this.system = bet.system;
		this.shopId = bet.shopId;
		this.taxFreePayoutAmount = bet.taxFreePayoutAmount;
		this.totalNumberOfCombinations = bet.totalNumberOfCombinations;
		this.totalNumberOfOffersPerBetSlip = bet.totalNumberOfOffersPerBetSlip;
		this.userId = bet.userId;
		this.dateCreated = bet.dateCreated;
		this.dateUpdated = bet.dateUpdated;
		this.id = bet.id;
		this.shopAddress = bet.shopAddress;
		this.liveBetSlipCountdown = bet.liveBetSlipCountdown;
		this.isWalletManuallyDisabled = bet.isWalletManuallyDisabled;
		this.displayedAmount = this.calculatedCashoutAmount?.toFixed(2);
		this.indicator = 0;
		this.isRemoved = false;
		this.currency =
			App.state.rootStore.userAuthStore.userAccounts?.find(
				(acc) => acc.accountTypeId === this.bettingAccountTypeId
			)?.currency.displayCode ||
			ISOToDisplayCode(THEME_SETTINGS.defaultCurrency);
		this.displayPaymentPerCombination = getDisplayedAmount(
			this.paymentPerCombination,
			this.currency
		);
		this.displayPayment = getDisplayedAmount(this.payment, this.currency);
		this.displayHandlingFee = getDisplayedAmount(
			this.handlingFee,
			this.currency
		);
		this.displayGainTax = getDisplayedAmount(this.gainTax, this.currency);
		this.displayGainBonus = getDisplayedAmount(
			this.gainBonus,
			this.currency
		);
		this.displayMaxGain = getDisplayedAmount(this.maxGain, this.currency);
		this.displayGain = getDisplayedAmount(this.gain, this.currency);
		this.displayPayout = getDisplayedAmount(this.payout, this.currency);
	}

	injectIsMultiToOffers(offerGroupsByEvent: BetOffer[][]) {
		for (const eventOffers of offerGroupsByEvent) {
			if (eventOffers.length > 1) {
				for (let i = 1; i < eventOffers.length; i++) {
					eventOffers[i].isMulti = true;
					eventOffers[i].multiEventName = `${i + 1} Multi`;
				}
			}
		}
	}

	groupOffersByEvent(offers: BetOffer[]): BetOffer[][] {
		const offerGroupsByEvent = new Map();

		for (const offer of offers) {
			const eventId = offer.eventId;

			if (!offerGroupsByEvent.has(eventId)) {
				offerGroupsByEvent.set(eventId, []);
			}

			offerGroupsByEvent.get(eventId).push(offer);
		}

		return Array.from(offerGroupsByEvent.values());
	}

	//#endregion object assign
}
